import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import axios from "axios";
import { ArrowLeft, Loader2, Globe, Instagram, Linkedin, Twitter, Youtube, Github, Facebook, Music, Mail } from "lucide-react";
import { useTranslation } from "@/i18n/LanguageContext";
import LangSwitcher from "@/components/LangSwitcher";
import { siteUrl } from "@/components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Iconset for common social platforms — falls back to Globe for unknown keys.
const SOCIAL_ICON = {
  instagram: Instagram,
  linkedin: Linkedin,
  twitter: Twitter,
  x: Twitter,
  youtube: Youtube,
  github: Github,
  facebook: Facebook,
  tiktok: Music,
  spotify: Music,
  website: Globe,
  email: Mail,
};

// Turn a raw url into a hostname-only visible label.
function hostOf(url) {
  try { return new URL(url).hostname.replace(/^www\./, ""); }
  catch { return url; }
}

export default function PublicMemberPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [member, setMember] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API}/members/${slug}`);
        setMember(data);
      } catch (e) {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#050505]">
        <Loader2 className="animate-spin text-[#F5F5F5]" size={28} />
      </div>
    );
  }

  if (notFound || !member) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#050505] text-white">
        <Helmet>
          <title>{`${t('vc.member-not-found')} · VangardClub`}</title>
          <meta name="robots" content="noindex" />
        </Helmet>
        <div className="text-center space-y-3">
          <p className="text-2xl font-serif">{t('vc.member-not-found')}</p>
          <Link to="/" className="text-[#F5F5F5] underline underline-offset-4">{t('vc.back-to-home')}</Link>
        </div>
      </div>
    );
  }

  const {
    name, bio, picture, social_links: socials = {}, member_id, member_since, slug: slugFinal,
    headline, company, role, industry, location, website, expertise = [], is_public,
  } = member;
  const displayName = name || t('vc.member');
  const origin = siteUrl;
  const canonical = `${origin}/u/${slugFinal || slug}`;
  const description = (bio && bio.trim()) || headline || `${displayName} · ${t('vc.member')}${member_id ? ` #${member_id}` : ""}.`;
  const image = picture || `${origin}/og-image.jpg`;
  const indexable = !!is_public;

  // JSON-LD Person schema — sameAs collects every social URL for entity linking.
  const sameAs = Object.values(socials).filter((v) => typeof v === "string" && v.startsWith("http"));
  if (website) sameAs.push(website);
  const personSchema = {
    "@context": "https://schema.org",
    "@type": "Person",
    name: displayName,
    description,
    image,
    url: canonical,
    identifier: member_id || undefined,
    jobTitle: role || headline || undefined,
    worksFor: company ? { "@type": "Organization", name: company, url: website || undefined } : undefined,
    address: location ? { "@type": "PostalAddress", addressLocality: location } : undefined,
    knowsAbout: expertise.length ? expertise : undefined,
    memberOf: {
      "@type": "Organization",
      name: "VangardClub",
      url: origin,
    },
    sameAs: sameAs.length ? sameAs : undefined,
  };
  const breadcrumb = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "VangardClub", item: origin },
      { "@type": "ListItem", position: 2, name: displayName, item: canonical },
    ],
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white noise-overlay">
      <Helmet>
        <title>{`${displayName} · VangardClub Member`}</title>        <meta name="description" content={description} />
        <link rel="canonical" href={canonical} />
        <meta name="robots" content={indexable ? "index, follow" : "noindex, nofollow"} />
        <meta property="og:type" content="profile" />
        <meta property="og:title" content={`${displayName} · VangardClub`} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content={image} />
        <meta property="og:url" content={canonical} />
        <meta property="og:site_name" content="VangardClub" />
        <meta property="profile:first_name" content={displayName.split(" ")[0] || ""} />
        <meta property="profile:last_name" content={displayName.split(" ").slice(1).join(" ") || ""} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${displayName} · VangardClub`} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={image} />
        <script type="application/ld+json">{JSON.stringify(personSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumb)}</script>
      </Helmet>

      <header className="border-b border-white/5">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <button onClick={() => navigate("/")}
            className="flex items-center gap-2 text-[#A3A3A3] hover:text-[#F5F5F5] transition-colors"
            data-testid="public-back-btn">
            <ArrowLeft size={16} /> <span className="text-sm">VangardClub</span>
          </button>
          <div className="flex items-center gap-4">
            <LangSwitcher />
            <Link to="/login" className="text-xs text-[#F5F5F5] hover:underline">{t('vc.sign-in')}</Link>
          </div>
        </div>
      </header><motion.main
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="max-w-4xl mx-auto px-6 py-10 sm:py-16"
        data-testid="public-profile-page"
      >
        <article itemScope itemType="https://schema.org/Person" className="space-y-8 sm:space-y-10">
          <div className="flex flex-col sm:flex-row sm:items-start gap-5 sm:gap-6">
            {picture ? (
              <img
                src={picture}
                alt={t('vc.member-portrait-alt', { name: displayName })}
                itemProp="image"
                className="w-24 h-24 sm:w-32 sm:h-32 rounded-2xl object-cover border border-white/20 grayscale"
                loading="eager"
              />
            ) : (
              <div className="w-24 h-24 sm:w-32 sm:h-32 rounded-2xl flex items-center justify-center bg-[#0F0F0F] border border-white/15">
                <span className="text-3xl sm:text-4xl font-serif text-neutral-300">
                  {displayName.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase()}
                </span>
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-[0.3em] text-neutral-400">{t('vc.member')}</p>
              <h1 itemProp="name" className="font-serif text-3xl sm:text-4xl md:text-5xl mt-2 leading-[1.05]">{displayName}</h1>
              {(role || company || headline) && (
                <p className="text-sm text-neutral-300 mt-2.5 font-light">
                  {[role, company].filter(Boolean).join(" · ") || headline}
                </p>
              )}
              {member_id && (
                <p className="text-[11px] text-neutral-500 mt-2 tracking-wide" itemProp="identifier">
                  {t('vc.member-no')} {member_id}{member_since ? ` · ${t('vc.joined')} ${new Date(member_since).getFullYear()}` : ""}
                </p>
              )}
            </div>
          </div>

          {(industry || location || website || expertise.length > 0) && (
            <section className="rounded-2xl border border-white/[0.08] bg-[#0A0A0A]/80 divide-y divide-white/[0.06]">
              {industry && (
                <div className="px-5 py-3.5 flex items-center justify-between gap-4">
                  <span className="text-[9px] tracking-[0.3em] uppercase text-neutral-500">{t('vc.industry')}</span>
                  <span className="text-sm text-neutral-200">{industry}</span>
                </div>
              )}
              {location && (
                <div className="px-5 py-3.5 flex items-center justify-between gap-4">
                  <span className="text-[9px] tracking-[0.3em] uppercase text-neutral-500">{t('vc.location')}</span>
                  <span className="text-sm text-neutral-200">{location}</span>
                </div>
              )}
              {website && (
                <div className="px-5 py-3.5 flex items-center justify-between gap-4">
                  <span className="text-[9px] tracking-[0.3em] uppercase text-neutral-500">{t('vc.website')}</span>
                  <a href={website} target="_blank" rel="noreferrer" itemProp="url" className="text-sm text-neutral-200 hover:text-white truncate max-w-[60%]">{hostOf(website)}</a>
                </div>
              )}
              {expertise.length > 0 && (
                <div className="px-5 py-4">
                  <span className="text-[9px] tracking-[0.3em] uppercase text-neutral-500">{t('vc.expertise')}</span>
                  <div className="flex flex-wrap gap-1.5 mt-2.5">
                    {expertise.map((e) => (
                      <span key={e} className="rounded-full border border-white/[0.12] px-3 py-1 text-[10px] tracking-[0.15em] uppercase text-neutral-300">{e}</span>
                    ))}
                  </div>
                </div>
              )}
            </section>
          )}

          {bio && (
            <section aria-labelledby="bio-heading" className="max-w-2xl">
              <h2 id="bio-heading" className="text-[10px] uppercase tracking-[0.3em] text-[#A3A3A3] mb-3">
                {t('vc.bio')}
              </h2>
              <p itemProp="description" className="text-base leading-relaxed text-white/85 whitespace-pre-wrap">
                {bio}
              </p>
            </section>
          )}

          {Object.keys(socials).length > 0 && (
            <section aria-labelledby="links-heading">
              <h2 id="links-heading" className="text-[10px] uppercase tracking-[0.3em] text-[#A3A3A3] mb-3">
                {t('vc.links')}
              </h2>
              <ul className="flex flex-wrap gap-2">
                {Object.entries(socials).map(([platform, url]) => {
                  const Icon = SOCIAL_ICON[platform.toLowerCase()] || Globe;
                  return (
                    <li key={platform}>
                      <a
                        href={url}
                        target="_blank"
                        rel="me noopener noreferrer"
                        itemProp="sameAs"
                        className="inline-flex items-center gap-2 px-3 py-2 border border-white/10 hover:border-[#F5F5F5]/60 hover:bg-white/5 transition-colors text-sm"
                        aria-label={t('vc.on-platform', { name: displayName, platform })}
                        data-testid={`social-${platform}`}
                      >
                        <Icon size={14} className="text-[#F5F5F5]" />
                        <span className="capitalize text-white/90">{platform}</span>
                        <span className="text-[10px] text-[#A3A3A3]">{hostOf(url)}</span>
                      </a>
                    </li>
                  );
                })}
              </ul>
            </section>
          )}
        </article>
      </motion.main>
    </div>
  );
}
